import { AvatarBaseIcon } from '@vizit/icons';
import { cva, cx } from '@vizit/theme';
import { AvatarColor, AvatarSize, avatarColorMap } from './types';

interface AvatarProps {
  /**
   * The content to display inside of the avatar.
   *
   * @optional
   * @type React.ReactNode
   */
  children?: React.ReactNode;
  /**
   * The background color of the avatar.
   *
   * @optional
   * @type AvatarColor
   */
  color?: AvatarColor;
  /**
   * Additional classes to apply to the avatar.
   *
   * @optional
   * @type string
   */
  className?: string;
  /**
   * The size of the avatar.
   *
   * @optional
   * @default 40
   * @type AvatarSize
   */
  size?: AvatarSize;
  /**
   * Add or remove the border around the avatar.
   *
   * @optional
   * @default true
   * @type boolean
   */
  border?: boolean;
}

const classes = cva({
  base: 'relative flex items-center justify-center text-white fill-brand-700',
  variants: {
    size: {
      [AvatarSize.XSmall]: 'h-12 w-12 text-xs',
      [AvatarSize.Small]: 'h-16 w-16 text-sm',
      [AvatarSize.Medium]: 'h-20 w-20 text-md',
      [AvatarSize.Large]: 'h-24 w-24 text-lg',
      [AvatarSize.XLarge]: 'h-28 w-28 text-xl',
    },
  },
});

/**
 * The Avatar component is used to display a user's avatar.
 *
 * @param props - The props for the Avatar component.
 * @example
 * <Avatar>
 *   <User01Icon />
 * </Avatar>
 */
function Avatar(props: AvatarProps) {
  const { children, color = AvatarColor.Gray, className = '', size = AvatarSize.Medium } = props;

  return (
    <div className={cx(classes({ size }), className)}>
      <AvatarBaseIcon className="absolute h-full w-full" fillColor={avatarColorMap[color]} />
      <div className="absolute">{children}</div>
    </div>
  );
}

export { Avatar };
